// ============================================================================
// AutoObjectForm — ФОРМА ОБЪЕКТА (тонкий компонент, РЕФАКТОРИНГ 1:1): типы +
// вызов useObjectFormModel (ВСЯ императивная модель — состояния/эффекты/
// вычисляемые значения/обработчики — вынесена в хук без изменения поведения)
// + JSX (кардвью-ветка и главный return). Здесь нет логики — только рендер.
// ============================================================================
import { useObjectFormModel } from '@/components/autoforms/useObjectFormModel';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { Button } from '@/components/ui/button';
import { ObjectFormWindowHeader } from '@/components/autoforms/ObjectFormWindowHeader';
import { ObjectCommandBar } from '@/components/autoforms/ObjectCommandBar';
import { AssigneePicker } from '@/components/autoforms/AssigneePicker';
import { PrintFormButton } from '@/components/autoforms/PrintFormButton';
import { ObjectFormBody } from '@/components/autoforms/ObjectFormBody';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { TagsBar } from '@/components/tags/TagsBar';
import { ObjectCardView } from '@/components/autoforms/ObjectCardView';
import { ContextChatPanel } from '@/components/chat/ContextChatPanel';
import { CameraCaptureDialog } from '@/components/media/CameraCaptureDialog';
import { VideoCallPanel, type InviteTarget } from '@/components/chat/VideoCallPanel';
import { getOrCreateContextThread } from '@/lib/chatBridge';
import { settingsStore } from '@/lib/settingsStore';
import { useState, useMemo } from 'react';
import { Phone } from 'lucide-react';

interface AutoObjectFormProps {
  objectType: string;
  objectName: string;
  objectId?: string;
  initialData?: Record<string, any>;
  onClose: () => void;
  /** Вызывается после создания НОВОЙ записи (для форм выбора) */
  onCreated?: (id: string, name: string) => void;
  /** inline=true — форма открывается КАК ЭКРАН внутри рабочей области
   *  (state-режим: не поверх, без оверлея; панели приложения на месте).
   *  default false = модальное окно (для пикеров/диалогов). */
  inline?: boolean;
}

export function AutoObjectForm({ objectType, objectName, objectId, initialData, onClose, onCreated, inline = false }: AutoObjectFormProps) {
  const model = useObjectFormModel({ objectType, objectName, objectId, initialData, onClose, onCreated, inline });
  const {
    type, fields, tabularSections, data, setData, dlCreate, setDlCreate, dlOpen, setDlOpen, loading, error, saveError,
    saving, moreOpen, setMoreOpen, customForm, formKey, customDisplayNames, setCustomDisplayNames,
    customTsData, setCustomTsData, formAttrs, fieldErrors, validationError, cardView, setCardView,
    geometryKey, formGeometry, tsParentId, table, lock, audit, auditEnabled, historyOpen,
    setHistoryOpen, catalogHierarchyType, catalogOwners, subordinates, activeTab, setActiveTab,
    formTitle, hiddenFields, pickerOpen, setPickerOpen, pickingAttr, multiPick, setMultiPick,
    modulePicker, setModulePicker, anyRefPicker, setAnyRefPicker, chatOpen, setChatOpen, chatUnread,
    canViewAudit, isAdmin, currentUser, statusSchema, statusPanel, statusBusy, applyStatus, fileBusy,
    fileError, handleFileUpload, handleFileDownload, renderField, noObjectRight, isPosted, actionBar,
    isDocument, isFileCatalog, customFormLayout, customFormObjectAttrs, customFormTabularSections,
    formReadOnly, formEnabled, tabs, handleSave, handleSaveAndPost, handleUnpost, runCommand,
    assignOpen, assignBusy, setAssignOpen, handlePick, handlePickTs, handleDLCreate, handleDLOpen, handlePicked,
    doAssignExecutor,     handleAnyRefValueSelect, handleAnyRefFile, handleMultiSelect, handleModuleSelect,
    filesLinked, hiddenObjectCommands, closeForm,
    transferOpen, transferBusy, setTransferOpen, doTransfer,
  } = model;

  // Камера в командной панели (объект с вкладкой «Файлы») — снимок через тот же
  // механизм загрузки, что FileListTab (SSoT — chunkedUpload + гео «при возможности»)
  const [cameraOpen, setCameraOpen] = useState(false);
  const attachHeaderFiles = async (files: File[]) => {
    if (!objectId || files.length === 0) return;
    // ЕДИНАЯ загрузка (chunkedUpload — offline-first) + координаты «всегда при
    // возможности»; НЕ дублирует кнопки вкладки (тот же механизм).
    const { uploadFile } = await import('@/lib/chunkedUpload');
    const { captureLocation } = await import('@/lib/geolocation');
    const { useToastStore } = await import('@/store/toastStore');
    let geoLat = 0; let geoLon = 0;
    try {
      const p = await captureLocation();
      geoLat = p.lat; geoLon = p.lon;
    } catch (e: any) {
      // Предупреждение (не ошибка загрузки!): файлы будут прикреплены БЕЗ координат.
      useToastStore.getState().showWarning('Файл будет загружен без координат: ' + (e?.message || 'не удалось определить местоположение'));
    }
    for (const f of files) {
      await uploadFile(f, { ownerId: objectId, geoLat, geoLon });
    }
  };
  const canAttachFiles = !!(objectId && filesLinked && !noObjectRight && !loading && !error && !lock.conflict);

  // Ad-hoc звонок «из карточки»: комната adhoc_{uuid}, НЕ привязана к чату.
  // Ссылки-приглашения сервера привязаны к чату, поэтому для ad-hoc цель ссылки —
  // ОБСУЖДЕНИЕ объекта (ближайший существующий механизм, без новых полей сервера).
  const [callOpen, setCallOpen] = useState(false);
  const callRoom = useMemo(() => `adhoc_${crypto.randomUUID()}`, []);
  const callTitle = String(data.name ?? data.code ?? data.number ?? objectName ?? '');
  const canCall = !!(objectId && !noObjectRight && !loading && !error && !lock.conflict);
  const resolveCallInvite = async (): Promise<InviteTarget> => {
    const t = await getOrCreateContextThread({ objectType: type, objectCode: objectName, objectId: objectId || '', title: callTitle });
    return {
      conversationId: t.conversationId,
      title: callTitle,
      note: 'Для звонка из карточки серверные ссылки-приглашения создаются для обсуждения объекта (ссылки привязаны к чату). Участник войдёт в обсуждение, откуда сможет присоединиться к звонку.',
    };
  };

  // Существующая задача/заявка: в шапке на месте «Сохранить/Отмена» — стандартные
  // команды объекта (Выполнено/Отменить/Удалить). Новая запись — обычные кнопки.
  const isTaskOrOrder = type === 'task' || type === 'order';
  const hideSaveCancel = !!objectId && isTaskOrOrder;

  // Заголовок: у ЗАЯВКИ добавляем НОМЕР — видно в шапке окна и в карточке
  // формы («Заявка · №000014 — Редактирование»), как в 1С.
  const displayTitle = type === 'order' && String((data as Record<string, unknown>)?.number || '').trim() !== ''
    ? `${formTitle} · №${String((data as Record<string, unknown>).number).trim()}`
    : formTitle;

  // Вид объекта «Карточка» (как в 1С): просмотр полей по важности,
  // «Редактировать» → обычная форма
  if (cardView && objectId && !error && !saveError) {
    return (
      <ObjectCardView
        fields={fields}
        data={data}
        formTitle={displayTitle}
        inline={inline}
        maximized={formGeometry?.maximized}
        pos={formGeometry?.pos}
        onGeometryChange={(g) => settingsStore.setJSON(geometryKey, g)}
        onEdit={() => setCardView(false)}
        onClose={closeForm}
      />
    );
  }

  return (
    <>
    <UniversalWindow
      open
      onClose={closeForm}
      title={displayTitle}
      blocking={false}
      initialMaximized={formGeometry?.maximized}
      initialPos={formGeometry?.pos}
      onGeometryChange={(g) => settingsStore.setJSON(geometryKey, g)}
      topBar={
        <ObjectFormWindowHeader
          noObjectRight={noObjectRight}
          objectId={objectId}
          markedRecord={!!objectId && (type === 'task' || type === 'order') && !!(data as any)?.is_marked}
          canDeletePermanent={!!objectId && (type === 'task' || type === 'order') && isAdmin}
          canTransfer={!!objectId && type === 'task' && !formReadOnly && formEnabled}
          isDocument={isDocument}
          saving={saving}
          loading={loading}
          hasLockConflict={!!lock.conflict}
          formReadOnly={formReadOnly}
          formEnabled={formEnabled}
          isPosted={isPosted}
          actionBar={actionBar}
          tabs={tabs}
          activeTab={activeTab}
          chatUnread={chatUnread}
          moreOpen={moreOpen}
          onMoreToggle={() => setMoreOpen((v) => !v)}
          onClose={onClose}
          onSave={() => void handleSave()}
          onSaveAndPost={(andClose) => void handleSaveAndPost(andClose)}
          onUnpost={() => void handleUnpost()}
          onRunCommand={(command) => runCommand(command)}
          onOpenChat={() => setChatOpen(true)}
          onTabChange={setActiveTab}
          hideSaveCancel={hideSaveCancel}
          showSave={hideSaveCancel ? !noObjectRight && !formReadOnly && formEnabled : true}
          commandBar={hideSaveCancel ? (
            <ObjectCommandBar
              entityType={type}
              objectId={objectId}
              disabled={formReadOnly || !formEnabled}
              hideCodes={hiddenObjectCommands}
              onCommand={runCommand}
            />
          ) : undefined}
          printButton={(
            <PrintFormButton
              objectType={type}
              objectName={objectName}
              recordId={objectId}
              disabled={formReadOnly || !formEnabled || noObjectRight}
            />
          )}
          callButton={canCall && customFormLayout?.callButton !== false ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCallOpen(true)}
              title="Позвонить (видеозвонок)"
            >
              <Phone className="h-3.5 w-3.5" />
            </Button>
          ) : undefined}
          filesLinked={filesLinked}
          canAttachFiles={canAttachFiles}
          onFilesSelected={(files) => void attachHeaderFiles(files)}
          onOpenCamera={() => setCameraOpen(true)}
        />
      }
    >
      <ObjectFormBody
        fields={fields}
        objectName={objectName}
        objectType={type}
        objectId={objectId}
        approval={objectId ? (
          <ApprovalPanel entityType={type} entityCode={objectName} entityId={objectId} />
        ) : undefined}
        tags={objectId ? (
          <TagsBar subjectType={type} subjectCode={objectName} subjectId={objectId} />
        ) : undefined}
        table={table}
        data={data}
        onDataChange={setData}
        customForm={customForm}
        customFormLayout={customFormLayout}
        customFormObjectAttrs={customFormObjectAttrs}
        customFormTabularSections={customFormTabularSections}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        subordinates={subordinates}
        statusSchema={statusSchema}
        statusPanel={statusPanel}
        statusBusy={statusBusy}
        onApplyStatus={(code) => void applyStatus(code)}
        audit={{ lastRecord: audit.lastRecord, records: audit.records, loading: audit.loading, error: audit.error }}
        auditEnabled={auditEnabled}
        canViewAudit={canViewAudit}
        historyOpen={historyOpen}
        onHistoryToggle={() => setHistoryOpen((v) => !v)}
        loading={loading}
        error={error}
        saveError={saveError}
        lockConflict={lock.conflict}
        isAdmin={isAdmin}
        onRequestRelease={(lockId) => lock.requestRelease(lockId, 'Пользователь просит освободить объект для редактирования')}
        onForceRelease={(lockId) => lock.forceRelease(lockId)}
        formReadOnly={formReadOnly || (type === 'task' && !!objectId)}
        formEnabled={formEnabled}
        validationError={validationError}
        formKey={formKey}
        displayNames={customDisplayNames}
        onDisplayNamesChange={setCustomDisplayNames}
        tsData={customTsData}
        onTsDataChange={setCustomTsData}
        hiddenFields={hiddenFields}
        formAttrs={formAttrs}
        tabularSections={tabularSections}
        tsParentId={tsParentId}
        isFileCatalog={isFileCatalog}
        fileBusy={fileBusy}
        fileError={fileError}
        fileLocked={!!lock.conflict}
        fileDownloadable={!!objectId && !!data.file_key}
        fileDisplayName={String(data.file_name ?? data.name ?? '')}
        fileSizeText={data.file_size ? ` • ${Number(data.file_size)} байт` : ''}
        onFileUpload={handleFileUpload}
        onFileDownload={handleFileDownload}
        renderField={renderField}
        fieldErrors={fieldErrors}
        catalogHierarchyType={catalogHierarchyType}
        catalogOwners={catalogOwners}
        pickerOpen={pickerOpen}
        pickingAttr={pickingAttr}
        anyRefPicker={anyRefPicker}
        multiPick={multiPick}
        modulePicker={modulePicker}
        onPickerClose={() => setPickerOpen(false)}
        onPickerSelect={handlePicked}
        onAnyRefClose={() => setAnyRefPicker(null)}
        onAnyRefTypeSelect={(t) => setAnyRefPicker((p) => p && { ...p, pickingType: t })}
        onAnyRefValueSelect={handleAnyRefValueSelect}
        onAnyRefFile={handleAnyRefFile}
        onMultiClose={() => setMultiPick(null)}
        onMultiSelect={handleMultiSelect}
        onModuleClose={() => setModulePicker(null)}
        onModuleSelect={handleModuleSelect}
        dlCreate={dlCreate}
        dlOpen={dlOpen}
        assignOpen={assignOpen}
        assignBusy={assignBusy}
        assignTitle={String(data.name ?? data.number ?? '')}
        onDlCreateClose={() => setDlCreate(null)}
        onDlOpenClose={() => setDlOpen(null)}
        onAssignClose={() => { if (!assignBusy) setAssignOpen(false); }}
        onAssignSelect={(id) => { if (id) void doAssignExecutor(id); }}
        onPick={handlePick}
        onPickTs={handlePickTs}
        onCreateElement={handleDLCreate}
        onOpenElement={handleDLOpen}
        onPickMulti={(tsName, colMeta) => { setMultiPick({ tsName, colMeta }); }}
        onCommand={runCommand}
      />
      {/* Контекстное обсуждение объекта (выдвижная панель/полный экран) */}
      <ContextChatPanel
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        objectType={type}
        objectCode={objectName}
        objectId={objectId || ''}
        objectTitle={String(data.name ?? data.code ?? data.number ?? objectName ?? '')}
        myId={currentUser?.id || ''}
        isAdmin={isAdmin}
      />
      {/* Камера из командной панели: снимок → attachHeaderFiles (та же загрузка) */}
      <CameraCaptureDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCaptured={({ file }) => { setCameraOpen(false); void attachHeaderFiles([file]); }}
      />
    </UniversalWindow>
    {/* Ad-hoc звонок из карточки: рендерим ВНЕ окна (UniversalWindow имеет
        transform → position:fixed внутри него считается от окна и обрезается);
        комната adhoc_{uuid}, ссылка-приглашение — обсуждение объекта. */}
    {callOpen && (
      <VideoCallPanel
        roomName={callRoom}
        identity={currentUser?.id || ''}
        myName={currentUser?.name || ''}
        onLeave={() => setCallOpen(false)}
        getInviteTarget={resolveCallInvite}
      />
    )}
    {/* «Передать задачу» (стандартная команда transfer): выбор исполнителя
        (AssigneePicker — загруженность) → TaskService.TransferTask (сервер:
        право task:edit/админ; статус не меняется; идемпотентно). */}
    {transferOpen && (
      <UniversalWindow open onClose={() => { if (!transferBusy) setTransferOpen(false); }} title="Передать задачу" width="w-[min(480px,94vw)]" resizable={false}>
        <div className="p-2 space-y-2">
          <p className="text-[11px] text-muted-foreground">
            Задача будет назначена выбранному исполнителю (статус не меняется).
          </p>
          <AssigneePicker
            value=""
            taskType={objectName}
            disabled={transferBusy}
            onChange={(id) => { if (id) void doTransfer(id); }}
          />
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => setTransferOpen(false)} disabled={transferBusy}>
              {transferBusy ? 'Передаём…' : 'Отмена'}
            </Button>
          </div>
        </div>
      </UniversalWindow>
    )}
    </>
  );

}
